// SCR-STAFF-804 (AP-CMP-02؛ US-CMP-040 تا 043؛ AC-CMP-040.1..2، 041.1..2، 042.1، 043.1): صورت‌وضعیت دوره — تولید برای ماه شمسی (هر قلم Earned فقط یک بار)،
// پیش‌نویس قابل حذف، تأیید (cmp.statement.approve)، حل اعتراض با پذیرش یا رد و اصلاحیه اختیاری، و نرخ‌های مالیات تکلیفی با تاریخ اثر (C-AGENT-1379).
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type CmpStatementDisputeView,
  type CmpStatementStatus,
  type CmpStatementSummaryView,
  type CmpWithholdingRateView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  toJalali,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { compensationKeys, fromBasisPoints, statementTone, toBasisPoints } from './common';
import { StatementDetail } from './StatementDetail';

export const SCR_STAFF_804 = 'SCR-STAFF-804';

export const STATEMENT_STATUSES: readonly CmpStatementStatus[] = [
  'Draft',
  'Approved',
  'Disputed',
  'Acknowledged',
  'PartiallyPaid',
  'Paid',
];

/** همه کلیدهای وابسته به صورت‌وضعیت و پرداخت. */
export const settlementScopes = (tenantId: string | undefined) => [
  compensationKeys.statements(tenantId),
  compensationKeys.payouts(tenantId),
  compensationKeys.entries(tenantId),
  compensationKeys.lines(tenantId),
];

export function StatementsPage() {
  const { t } = useTranslation('cmp');
  const access = useAccess();
  const write = useWriteAccess('cmp.statement.manage');
  const [tab, setTab] = useState<'statements' | 'withholding'>('statements');
  const [generating, setGenerating] = useState(false);
  const canManage = access.can('cmp.statement.manage') && write.canWrite;

  return (
    <ListPage
      screenId={SCR_STAFF_804}
      titleKey="cmp:statements.title"
      subtitleKey="cmp:statements.subtitle"
      actions={
        access.can('cmp.statement.manage') ? (
          <Button
            variant="contained"
            onClick={() => setGenerating(true)}
            disabled={!write.canWrite}
            data-testid="statements-generate"
          >
            {t('cmp:statements.generate')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack spacing={1}>
          {access.can('cmp.statement.manage') && <WriteNotice write={write} />}
          <Tabs value={tab} onChange={(_, value: 'statements' | 'withholding') => setTab(value)}>
            <Tab
              value="statements"
              label={t('cmp:statements.tab_statements')}
              data-testid="statements-tab-list"
            />
            <Tab
              value="withholding"
              label={t('cmp:statements.tab_withholding')}
              data-testid="statements-tab-withholding"
            />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'statements' ? (
        <StatementsTable
          canManage={canManage}
          canApprove={access.can('cmp.statement.approve') && write.canWrite}
        />
      ) : (
        <WithholdingTable canManage={canManage} />
      )}
      {generating && <GenerateDialog onClose={() => setGenerating(false)} />}
    </ListPage>
  );
}

function StatementsTable({ canManage, canApprove }: { canManage: boolean; canApprove: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const [status, setStatus] = useState<CmpStatementStatus | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);
  const statements = useQuery({
    queryKey: [...compensationKeys.statements(session?.tenantId), status],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/statements', {
          params: { query: { status: status || undefined } },
        }),
      ),
  });

  const columns: DataTableColumn<CmpStatementSummaryView>[] = [
    {
      id: 'beneficiary',
      headerKey: 'cmp:statements.columns.beneficiary',
      cell: (row) => row.beneficiaryName ?? '—',
    },
    {
      id: 'period',
      headerKey: 'cmp:statements.period',
      cell: (row) => (
        <>
          <JalaliDate value={row.periodFrom} /> – <JalaliDate value={row.periodTo} />
        </>
      ),
    },
    {
      id: 'status',
      headerKey: 'cmp:ledger.columns.status',
      cell: (row) => (
        <StatusChip tone={statementTone(row.status)} labelKey={`cmp:statement_status.${row.status}`} />
      ),
    },
    {
      id: 'gross',
      headerKey: 'cmp:statements.gross',
      cell: (row) => <Money amountMinor={Number(row.gross.amount)} />,
      align: 'right',
    },
    {
      id: 'deductions',
      headerKey: 'cmp:statements.deductions',
      cell: (row) => <Money amountMinor={Number(row.deductions.amount)} />,
      align: 'right',
    },
    {
      id: 'net',
      headerKey: 'cmp:statements.net',
      cell: (row) => <Money amountMinor={Number(row.net.amount)} />,
      align: 'right',
    },
    {
      id: 'payable',
      headerKey: 'cmp:statements.payable',
      cell: (row) => <Money amountMinor={Number(row.payable.amount)} />,
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'cmp:ledger.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setOpenId(row.id)} data-testid={`statement-open-${row.id}`}>
          {t('cmp:agreements.open')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <TextField
        select
        size="small"
        label={t('cmp:ledger.filters.status')}
        value={status}
        onChange={(event) => setStatus(event.target.value as CmpStatementStatus | '')}
        sx={{ minWidth: 200, mb: 2 }}
      >
        <MenuItem value="">{t('cmp:ledger.filters.all')}</MenuItem>
        {STATEMENT_STATUSES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`cmp:statement_status.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <DataTable
        captionKey="cmp:statements.caption"
        columns={columns}
        rows={statements.data?.items}
        getRowId={(row) => row.id}
        isLoading={statements.isPending}
        error={statements.error}
        onRetry={() => void statements.refetch()}
        emptyTitleKey="cmp:statements.empty"
      />
      {openId && (
        <StatementDialog
          id={openId}
          canManage={canManage}
          canApprove={canApprove}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function StatementDialog({
  id,
  canManage,
  canApprove,
  onClose,
}: {
  id: string;
  canManage: boolean;
  canApprove: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<'approve' | 'discard' | null>(null);
  const [resolving, setResolving] = useState<CmpStatementDisputeView | null>(null);
  const statement = useQuery({
    queryKey: compensationKeys.statement(session?.tenantId, id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/compensation/statements/{id}', { params: { path: { id } } })),
  });
  const invalidate = () => {
    for (const key of settlementScopes(session?.tenantId)) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
  const approve = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/compensation/statements/{id}:approve', { params: { path: { id } } })),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const discard = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/compensation/statements/{id}:discard', { params: { path: { id } } })),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const view = statement.data;
  const draft = view?.summary.status === 'Draft';

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="statement-dialog-title">
      <DialogTitle id="statement-dialog-title">{t('cmp:statements.detail_title')}</DialogTitle>
      <DialogContent>
        {statement.error ? (
          <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />
        ) : (
          view && (
            <StatementDetail
              view={view}
              disputeActions={(dispute) =>
                canManage && dispute.status === 'Open' ? (
                  <Stack direction="row">
                    <Button
                      size="small"
                      onClick={() => setResolving(dispute)}
                      data-testid={`statement-dispute-resolve-${dispute.id}`}
                    >
                      {t('cmp:statements.resolve')}
                    </Button>
                  </Stack>
                ) : null
              }
            />
          )
        )}
      </DialogContent>
      <DialogActions>
        {draft && canManage && (
          <Button color="error" onClick={() => setConfirm('discard')} data-testid="statement-discard">
            {t('cmp:statements.discard')}
          </Button>
        )}
        {draft && canApprove && (
          <Button variant="contained" onClick={() => setConfirm('approve')} data-testid="statement-approve">
            {t('cmp:statements.approve')}
          </Button>
        )}
        <Button onClick={onClose}>{t('cmp:detail.close')}</Button>
      </DialogActions>
      <ConfirmDialog
        open={confirm !== null}
        titleKey={confirm === 'discard' ? 'cmp:statements.discard' : 'cmp:statements.approve'}
        messageKey={
          confirm === 'discard' ? 'cmp:statements.discard_confirm' : 'cmp:statements.approve_confirm'
        }
        confirmKey={confirm === 'discard' ? 'cmp:statements.discard' : 'cmp:statements.approve'}
        destructive={confirm === 'discard'}
        busy={approve.isPending || discard.isPending}
        error={(confirm === 'discard' ? discard.error : approve.error) ?? undefined}
        onConfirm={() => (confirm === 'discard' ? discard.mutate() : approve.mutate())}
        onCancel={() => {
          approve.reset();
          discard.reset();
          setConfirm(null);
        }}
      />
      {resolving && (
        <ResolveDialog
          statementId={id}
          dispute={resolving}
          onClose={() => setResolving(null)}
          onDone={invalidate}
        />
      )}
    </Dialog>
  );
}

const resolveSchema = z.object({
  accepted: z.enum(['accept', 'reject']),
  resolution: zRequiredText(500),
  adjustment: z.number().int().nullable(),
});
type ResolveForm = z.input<typeof resolveSchema>;

/** حل اعتراض (US-CMP-043): پذیرش با اصلاحیه اختیاری یا رد؛ قلم معترض پس از حل در پرداخت بعدی قرار می‌گیرد. */
function ResolveDialog({
  statementId,
  dispute,
  onClose,
  onDone,
}: {
  statementId: string;
  dispute: CmpStatementDisputeView;
  onClose: () => void;
  onDone: () => void;
}) {
  const { tenantApi } = useTenantSession();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<ResolveForm>({
    resolver: zodResolver(resolveSchema),
    defaultValues: { accepted: 'accept', resolution: '', adjustment: null },
  });
  const save = useMutation({
    mutationFn: (form: ResolveForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/statements/{id}/disputes/{disputeId}:resolve', {
          params: { path: { id: statementId, disputeId: dispute.id } },
          body: {
            accepted: form.accepted === 'accept',
            resolution: form.resolution.trim(),
            adjustment:
              form.accepted === 'accept' && form.adjustment
                ? { amount: form.adjustment, currency: 'IRR' }
                : null,
          },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['resolution', 'adjustment'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-804-RESOLVE"
      titleKey="cmp:statements.resolve_title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={control}
        name="accepted"
        labelKey="cmp:statements.resolve_decision"
        fullWidth
        options={[
          { value: 'accept', labelKey: 'cmp:statements.resolve_accept' },
          { value: 'reject', labelKey: 'cmp:statements.resolve_reject' },
        ]}
      />
      <MoneyFieldRHF
        control={control}
        name="adjustment"
        labelKey="cmp:statements.resolve_adjustment"
        helperKey="cmp:statements.resolve_adjustment_help"
        fullWidth
      />
      <TextFieldRHF
        control={control}
        name="resolution"
        labelKey="cmp:statements.resolution"
        required
        fullWidth
        multiline
        minRows={2}
        slotProps={{ htmlInput: { 'data-testid': 'statement-resolution' } }}
      />
    </FormDialog>
  );
}

/** سال و ماه شمسی جاری برای پیش‌فرض تولید. */
function currentJalali(): { year: number; month: number } {
  const [year = 1405, month = 1] = toJalali(new Date(), { pattern: 'yyyy/MM/dd', persianDigits: false })
    .split('/')
    .map(Number);
  return { year, month };
}

const generateSchema = z.object({
  year: z.string().regex(/^1[34]\d\d$/, { error: 'cmp:statements.year_invalid' }),
  month: z.string(),
});
type GenerateForm = z.input<typeof generateSchema>;

function GenerateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const now = currentJalali();
  const { control, handleSubmit, formState } = useForm<GenerateForm>({
    resolver: zodResolver(generateSchema),
    defaultValues: { year: String(now.year), month: String(now.month) },
  });
  const generate = useMutation({
    mutationFn: (form: GenerateForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/statements:generate', {
          body: { year: Number(form.year), month: Number(form.month), beneficiaryId: null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: compensationKeys.statements(session?.tenantId) });
    },
  });

  if (generate.data) {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="statements-generated-title">
        <DialogTitle id="statements-generated-title">{t('cmp:statements.generate')}</DialogTitle>
        <DialogContent data-testid="statements-generated">
          {t('cmp:statements.generated', {
            count: generate.data.items.length,
            value: faDigits(generate.data.items.length),
            skipped: faDigits(Number(generate.data.skipped)),
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('cmp:detail.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-804-GENERATE"
      titleKey="cmp:statements.generate_title"
      submitKey="cmp:statements.generate"
      onSubmit={() => void handleSubmit((form) => generate.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={generate.isPending}
      submitError={generate.error ?? undefined}
    >
      <Stack direction="row" spacing={2}>
        <TextFieldRHF
          control={control}
          name="year"
          labelKey="cmp:statements.year"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'statements-year' } }}
        />
        <SelectRHF
          control={control}
          name="month"
          labelKey="cmp:statements.month"
          fullWidth
          options={Array.from({ length: 12 }, (_, index) => ({
            value: String(index + 1),
            labelKey: `cmp:jalali_month.${index + 1}`,
          }))}
        />
      </Stack>
      <span>{t('cmp:statements.generate_help')}</span>
    </FormDialog>
  );
}

const withholdingSchema = z.object({
  effectiveFrom: z.string().min(1, { error: 'common:validation.required' }),
  rate: z
    .string()
    .refine((value) => value.trim() !== '' && Number(value) >= 0 && Number(value) <= 100, {
      error: 'cmp:simulator.rate_invalid',
    }),
  note: z.string(),
});
type WithholdingForm = z.input<typeof withholdingSchema>;

/** نرخ‌های مالیات تکلیفی با تاریخ اثر (AC-CMP-041.1؛ C-AGENT-1379). */
function WithholdingTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const key = [...compensationKeys.statements(session?.tenantId), 'withholding'];
  const rates = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/compensation/withholding-tax-rates')),
  });
  const { control, handleSubmit, formState, reset } = useForm<WithholdingForm>({
    resolver: zodResolver(withholdingSchema),
    defaultValues: { effectiveFrom: '', rate: '', note: '' },
  });
  const save = useMutation({
    mutationFn: (form: WithholdingForm) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/withholding-tax-rates', {
          body: {
            effectiveFrom: form.effectiveFrom,
            rateBasisPoints: toBasisPoints(Number(form.rate)),
            note: form.note.trim() || null,
          },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      reset();
      setAdding(false);
    },
  });
  const columns: DataTableColumn<CmpWithholdingRateView>[] = [
    {
      id: 'from',
      headerKey: 'cmp:statements.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    {
      id: 'rate',
      headerKey: 'cmp:statements.rate',
      cell: (row) => t('cmp:detail.rate', { rate: faDigits(fromBasisPoints(row.rateBasisPoints) ?? 0) }),
    },
    { id: 'note', headerKey: 'cmp:form.note', cell: (row) => row.note ?? '—' },
  ];
  return (
    <Stack spacing={2}>
      {canManage && (
        <Stack direction="row">
          <Button onClick={() => setAdding(true)} data-testid="withholding-add">
            {t('cmp:statements.add_rate')}
          </Button>
        </Stack>
      )}
      <DataTable
        captionKey="cmp:statements.withholding_caption"
        columns={columns}
        rows={rates.data?.items}
        getRowId={(row) => row.id}
        isLoading={rates.isPending}
        error={rates.error}
        onRetry={() => void rates.refetch()}
        emptyTitleKey="cmp:statements.withholding_empty"
      />
      <FormDialog
        open={adding}
        screenId="SCR-STAFF-804-WITHHOLDING"
        titleKey="cmp:statements.add_rate"
        onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
        onClose={() => setAdding(false)}
        isDirty={formState.isDirty}
        isSubmitting={save.isPending}
        submitError={save.error ?? undefined}
      >
        <JalaliDatePickerRHF
          control={control}
          name="effectiveFrom"
          labelKey="cmp:statements.effective_from"
          required
        />
        <TextFieldRHF
          control={control}
          name="rate"
          labelKey="cmp:statements.rate"
          required
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': 'withholding-rate' } }}
        />
        <TextFieldRHF control={control} name="note" labelKey="cmp:form.note" fullWidth />
      </FormDialog>
    </Stack>
  );
}
